import { useToast } from '@monorepo/shared/contexts/ToastContext';
import { useModal } from '@monorepo/shared/hooks/useModalV2';
import React, { useCallback } from 'react';

type UseConfirmDocumentDeleteParams = {
  documentName: string;
  onDelete: () => Promise<unknown>;
};

export function useConfirmDocumentDelete({
  documentName,
  onDelete,
}: UseConfirmDocumentDeleteParams) {
  const { confirm } = useModal();
  const { showUserFriendlyErrorToast, success } = useToast();

  const handleDeleteConfirm = useCallback(
    async (isConfirmed) => {
      if (isConfirmed) {
        await onDelete();
      }
    },
    [onDelete],
  );

  const handleDelete = useCallback(async () => {
    try {
      const isConfirmed = await confirm({
        title: 'Delete document',
        description: (
          <>
            Are you sure you want to delete <b>{documentName}</b>? This cannot
            be undone.
          </>
        ),
        cancelButtonText: 'Keep document',
        confirmButtonText: 'Delete document',
        afterConfirmButtonText: 'Deleting...',
        danger: true,
        onConfirmAsync: handleDeleteConfirm,
      });
      if (isConfirmed) {
        success(`Document "${documentName}" was deleted.`);
      }
    } catch (err) {
      showUserFriendlyErrorToast(
        err,
        `Unable to delete document "${documentName}".`,
      );
    }
  }, [
    confirm,
    documentName,
    handleDeleteConfirm,
    showUserFriendlyErrorToast,
    success,
  ]);

  return { handleDelete };
}
